/**
 * A change of plan or service — FR-E7, T-2.9. Split out of `CasePanels.tsx` in
 * Wave 15C; the behaviour is unchanged.
 *
 * **A change is proposed here and approved by somebody else.** `can_decide`
 * comes back per row from the server, which applies the firm's configured
 * approver *and* the rule that the proposer cannot approve their own. Neither
 * is re-derived here.
 *
 * **C27 (UAT reports #13, #19).** *To* and the reason are required and marked
 * with the red star; *To*'s *Choose…* is a placeholder rather than a choice, and
 * *Propose* pressed with either missing says so under the field.
 */

import { Alert, Badge, type BadgeTone, Button, EmptyState, Input, Select } from '@fiveox/ui'
import { Repeat } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'

import { RecordSection, humanise } from '../../components/page'
import {
  type CaseChange,
  type CaseDetail as Detail,
  caseFlows,
  changeControl,
} from '../../lib/api/cases'
import { type Plan, type Service, services } from '../../lib/api/services'
import { useAction } from './useAction'

/** A change's status has no kind in the status map (components/page/status.ts);
 *  these follow its rule — waiting on the firm is a warning, done well a
 *  success, refused an error. */
const CHANGE_TONE: Record<CaseChange['status'], BadgeTone> = {
  proposed: 'warning',
  approved: 'success',
  rejected: 'error',
}

/** The kit control's own label with `FormRow`'s red star (C27 item 11) — these
 *  two controls carry their labels rather than sitting in a `FormRow`. */
function RequiredLabel({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <span className="text-destructive" aria-hidden="true" data-required-star>
        {' *'}
      </span>
    </>
  )
}

export function CaseChanges({
  detail,
  canUpdate,
  onChanged,
}: {
  detail: Detail
  canUpdate: boolean
  onChanged: () => void
}) {
  const [rows, setRows] = useState<CaseChange[]>([])
  const [catalog, setCatalog] = useState<{ services: Service[]; plans: Plan[] }>({
    services: [],
    plans: [],
  })
  const [kind, setKind] = useState<'plan' | 'service'>('plan')
  const [target, setTarget] = useState('')
  const [reason, setReason] = useState('')
  const [tried, setTried] = useState(false)
  const { error, busy, run } = useAction()
  const control = changeControl(detail)

  const load = useCallback(async () => {
    setRows((await caseFlows.changes(detail.case.id)).data)
  }, [detail.case.id])

  useEffect(() => {
    void load().catch(() => undefined)
    void services
      .read(detail.case.service_id)
      // The service's own plans ride on the service row (`Service.plans`), so
      // the plan picker is one read and not one per plan.
      .then((body) => setCatalog((c) => ({ ...c, plans: body.data.service.plans ?? [] })))
      .catch(() => undefined)
    void services
      .list()
      .then((body) => setCatalog((current) => ({ ...current, services: body.data.services })))
      .catch(() => undefined)
  }, [detail.case.id, detail.case.service_id, load])

  const options = kind === 'plan' ? catalog.plans : catalog.services

  return (
    <RecordSection
      id="case-changes"
      icon={<Repeat size={18} aria-hidden />}
      title="Plan and service"
      description="Propose a different plan or service. Somebody else approves it."
    >
      <div className="space-y-4">
        {error && <Alert tone="error">{error}</Alert>}

        {rows.length === 0 ? (
          <EmptyState
            variant="outline"
            title="No changes proposed"
            description="The case is on the plan and service it opened with."
          />
        ) : (
          <ul className="space-y-3">
            {rows.map((row) => (
              <li key={row.id} className="border-border rounded-xl border p-4 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-foreground font-medium">Change of {row.kind}</span>
                  <Badge tone={CHANGE_TONE[row.status] ?? 'neutral'} size="sm">
                    {humanise(row.status)}
                  </Badge>
                </div>
                <p className="text-muted-foreground mt-1">{row.reason}</p>
                {row.status === 'proposed' && (
                  <>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {/* Snapshotted when the change was proposed, so a firm
                          that changes its approver next week does not
                          invalidate it. */}
                      Waiting on {row.approver_role ?? 'a partner'}.
                    </p>
                    {row.can_decide && (
                      <div className="mt-3 flex gap-2">
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              await caseFlows.decideChange(row.id, true)
                              await load()
                              onChanged()
                            }, 'That could not be approved.')
                          }
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              await caseFlows.decideChange(row.id, false)
                              await load()
                            }, 'That could not be refused.')
                          }
                        >
                          Refuse
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {canUpdate && !control.enabled && control.reason && (
          <Alert tone="info">{control.reason}</Alert>
        )}

        {canUpdate && control.enabled && (
          <div className="border-border space-y-3 border-t pt-4">
            <div className="grid gap-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
              <Select
                size="sm"
                label="Change"
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as 'plan' | 'service')
                  setTarget('')
                  setTried(false)
                }}
              >
                <option value="plan">the plan</option>
                <option value="service">the service</option>
              </Select>
              <Select
                size="sm"
                label={<RequiredLabel>To</RequiredLabel>}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                required
                aria-required="true"
                error={tried && !target ? `Choose the ${kind} it changes to.` : null}
              >
                <option value="" disabled hidden>
                  Choose…
                </option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Select>
            </div>
            <Input
              size="sm"
              label={<RequiredLabel>Why is it changing?</RequiredLabel>}
              hint="The approver acts on this."
              required
              aria-required="true"
              error={tried && !reason.trim() ? 'Say why — the approver acts on this.' : null}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {kind === 'service' && (
              <p className="text-muted-foreground text-xs">
                {/* The surprising one, said on the screen rather than found
                    afterwards. ADR-0009: a case in flight keeps the rules it
                    opened under. */}
                The case keeps its number, its history <em>and its current workflow</em> — the
                new service’s workflow applies to the next case, not this one.
              </p>
            )}
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                setTried(true)
                if (!target || !reason.trim()) return
                void run(async () => {
                  await caseFlows.proposeChange(detail.case.id, {
                    kind,
                    to_id: target,
                    reason: reason.trim(),
                  })
                  setTarget('')
                  setReason('')
                  setTried(false)
                  await load()
                }, 'The change could not be proposed.')
              }}
            >
              Propose the change
            </Button>
          </div>
        )}
      </div>
    </RecordSection>
  )
}
